import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, EyeOff, MessageCircle } from "lucide-react";
import { useState } from "react";
import { Stars } from "../../components/Stars";
import { api } from "../../lib/api";
import { useAuth } from "../../state/auth";

type ReviewStatus = "PENDING" | "PUBLISHED" | "HIDDEN";
type AdminReview = {
  publicId: string; reviewerName: string; rating: number; comment: string | null;
  status: ReviewStatus; createdAt: string; productName: string; orderNumber: string;
};
type ReviewPage = { items: AdminReview[]; total: number; offset: number; hasMore: boolean };

export function AdminReviewsPage() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ReviewStatus>("PENDING");
  const reviews = useQuery({
    queryKey: ["admin", "reviews", status],
    queryFn: () => api<ReviewPage>(`/admin/reviews?status=${status}&limit=50`, { token: accessToken })
  });
  const items = reviews.data?.items ?? [];
  const total = reviews.data?.total ?? 0;
  const setReviewStatus = useMutation({
    mutationFn: ({ publicId, next }: { publicId: string; next: ReviewStatus }) => api(`/admin/reviews/${publicId}`, {
      method: "PATCH", token: accessToken, body: JSON.stringify({ status: next })
    }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin", "reviews"] })
  });

  return <>
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div><p className="text-sm font-extrabold uppercase tracking-widest text-clay-dark">Customer feedback</p><h1 className="mt-2 font-display text-4xl font-bold">Reviews</h1><p className="mt-2 text-ink/55">Star ratings left by verified buyers on product pages.</p></div>
      <span className="rounded-full bg-butter/30 px-4 py-2 font-extrabold">{total} {status.toLowerCase()}</span>
    </div>
    <div className="mt-6 flex flex-wrap gap-2">
      {(["PENDING", "PUBLISHED", "HIDDEN"] as const).map((itemStatus) => <button key={itemStatus} onClick={() => setStatus(itemStatus)} className={`rounded-full px-4 py-2 text-sm font-extrabold ${status === itemStatus ? "bg-ink text-white" : "border border-ink/10 bg-white"}`}>{itemStatus.toLowerCase()}</button>)}
    </div>
    <div className="mt-5 grid gap-3">
      {reviews.isLoading && <p className="p-8 text-center text-ink/50">Loading reviews…</p>}
      {items.map((item) => <div key={item.publicId} className="rounded-[1.75rem] border border-ink/10 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><Stars value={item.rating} /><p className="mt-2 font-extrabold">{item.reviewerName}</p><p className="text-xs text-ink/45">{item.productName} · {item.orderNumber} · {new Date(item.createdAt).toLocaleString()}</p></div>
          <div className="flex gap-2">
            {item.status !== "PUBLISHED" && <button onClick={() => setReviewStatus.mutate({ publicId: item.publicId, next: "PUBLISHED" })} className="btn-secondary min-h-9 px-3 py-1.5 text-xs"><Check size={14} /> Publish</button>}
            {item.status !== "HIDDEN" && <button onClick={() => setReviewStatus.mutate({ publicId: item.publicId, next: "HIDDEN" })} className="btn-secondary min-h-9 px-3 py-1.5 text-xs"><EyeOff size={14} /> Hide</button>}
          </div>
        </div>
        {item.comment && <p className="mt-3 text-sm leading-relaxed text-ink/75">{item.comment}</p>}
      </div>)}
      {!reviews.isLoading && items.length === 0 && <div className="rounded-[1.75rem] border border-ink/10 bg-white p-12 text-center"><MessageCircle className="mx-auto text-sage" size={34} /><p className="mt-3 font-display text-2xl font-bold">No reviews here</p><p className="text-sm text-ink/50">New reviews appear automatically.</p></div>}
    </div>
  </>;
}
